import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function SalesRevenue() {
  const { user } = useAuth();
  const [data, setData] = useState({ orders: [], totalRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(''); // YYYY-MM-DD for admin; empty = all time
  const role = user && String(user.role).toLowerCase();
  const isSalesUser = role === 'custom_role_sales';
  const isAdmin = role === 'admin' || role === 'master_admin';

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const params = isAdmin && selectedDate ? { date: selectedDate } : undefined;
      const res = await apiService.orders.getSalesOrdersRevenue(params);
      setData({
        orders: res.data?.orders || [],
        totalRevenue: res.data?.totalRevenue ?? 0,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + new Date(d).toLocaleTimeString(undefined, { timeStyle: 'short' });
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-green-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Revenue</h1>
        <p className="text-gray-600 text-sm mb-2">
          {isSalesUser
            ? 'Revenue from orders where payment has been received — today only.'
            : isAdmin
            ? 'Revenue from sales orders where payment has been received. View all or filter by date.'
            : 'Revenue from sales orders where payment has been received.'}
        </p>
        {isAdmin && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Show revenue for:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
            />
            <button
              type="button"
              onClick={() => setSelectedDate('')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              All time
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && (
          <>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-gray-600">
                {isAdmin && selectedDate
                  ? `Total revenue for ${new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { dateStyle: 'long' })}`
                  : isAdmin
                  ? 'Total revenue (all time)'
                  : 'Total revenue'}
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-800 tabular-nums">SAR {Number(data.totalRevenue).toFixed(2)}</div>
            </div>
            {data.orders.length === 0 ? (
              <p className="text-gray-500 py-4">No orders with payment received yet.</p>
            ) : (
              <div className="space-y-3">
                {data.orders.map((o) => (
                  <div
                    key={o._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 px-3 sm:py-2 sm:px-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-gray-800">{o.orderNo}</span>
                      <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">{formatDate(o.dateTime)}</span>
                      {o.customerName && (
                        <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">· {o.customerName}</span>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800 tabular-nums flex-shrink-0">SAR {Number(o.totalAmount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
